import { Close } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';

import { Label } from '@/components';
import { useDebouncedValue } from '@/hooks';
import type { NewFeatureAlert as NewFeatureAlertType } from '@/types';

interface NewFeatureTooltipProps
  extends Omit<TooltipProps, 'title' | 'arrow' | 'slotProps' | 'open'> {
  id: NewFeatureAlertType; // Used for accessibility
  featureName: string;
  featureDescription: string;
  isVisible?: boolean;
  onDismiss?: () => void;
}

export const NewFeatureTooltip = ({
  featureName,
  featureDescription,
  isVisible,
  onDismiss,
  ...props
}: NewFeatureTooltipProps) => {
  const delayedOpen = useDebouncedValue(isVisible, 200) ?? false;

  const content = (
    <Box>
      <Box sx={styles.header}>
        <Box sx={styles.title}>
          <Label text="New" />
          <Typography variant="body2SemiBold" color="text.contrast">
            {featureName}
          </Typography>
        </Box>
        <IconButton aria-label="Dismiss" onClick={onDismiss}>
          <Close sx={styles.closeIcon} />
        </IconButton>
      </Box>
      <Box sx={styles.main}>
        <Typography variant="body3" color="text.contrast">
          {featureDescription}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={() => onDismiss?.()}>
      <Tooltip
        title={content}
        arrow
        slotProps={{ tooltip: { sx: styles.tooltip } }}
        open={delayedOpen}
        {...props}
      />
    </ClickAwayListener>
  );
};

const styles = {
  tooltip: {
    p: 0,
    position: 'relative',
    backgroundColor: 'primary.main',
    maxWidth: 400,
    '& .MuiTooltip-arrow': {
      color: 'primary.main',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    pl: 1.5,
    pr: 0.5,
    py: 0.5,
  },
  main: {
    px: 2,
    pb: 2,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  closeIcon: {
    color: 'text.contrast',
    fontSize: 16,
  },
};
